import React, { useState, useEffect, useRef, useCallback } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { python } from "@codemirror/lang-python";
import { cpp } from "@codemirror/lang-cpp";
import { java } from "@codemirror/lang-java";
import { csharp } from "@replit/codemirror-lang-csharp";
import { completionKeymap } from "@codemirror/autocomplete";
import { startCompletion } from "@codemirror/autocomplete";
import { Play, Trash2, Sun, Moon, FileText, CheckCircle, XCircle, AlertTriangle, Bot, Shield } from "lucide-react";
import { executeCode, updateCodeSandboxProgress, getSignedUrl } from "ServerAPI/serverAPI";
import { handleCodeReview } from "../../../../dashboard/user-landing-page/codeReviewUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useCookies } from "react-cookie";
import { ROLES } from "constants/Constants";
import { useParams } from "react-router-dom";
import { Button, CircularProgress, Tabs, Tab, Typography, TextField, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { oneDark, defaultLightThemeOption } from '@uiw/react-codemirror';
import Tooltip from '@mui/material/Tooltip';
import { createAssistanceExtensions } from "./codeAssistance";
import ChatComponent from './ChatComponent';
import useProctoring from "../proctoring/useProctoring";
import ProctoredCodeEditor from "./ProctoredCodeEditor";

const languageMap = {
    javascript: "javascript",
    python: "python",
    'c++': "g++",
    cpp: "g++",
    java: "java",
    "c#": "csharp",
    csharp: "csharp",
    c: "c",
};

const languageOptions = {
    javascript: { name: "JavaScript", extension: javascript, fileExtension: ".js" },
    python: { name: "Python", extension: python, fileExtension: ".py" },
    cpp: { name: "C++", extension: cpp, fileExtension: ".cpp" },
    java: { name: "Java", extension: java, fileExtension: ".java" },
    csharp: { name: "C#", extension: csharp, fileExtension: ".cs" },
    c: { name: "C", extension: cpp, fileExtension: ".c" },
};

const defaultVersions = {
    javascript: "18.15.0",
    python: "3.10.0",
    cpp: "10.2.0",
    java: "15.0.2",
    csharp: "5.0.201",
    c: "10.2.0",
};

const defaultSnippets = {
    python: "# Write your Python code here...",
    javascript: "// Write your JavaScript code here...",
    cpp: "// Write your C++ code here...",
    java: "// Write your Java code here...",
    csharp: "// Write your C# code here...",
    c: "// Write your c code here...",
};

const getNormalizedLanguage = (language) => {
    const lang = language?.toLowerCase()?.trim();
    if (!lang) return "python";
    if (lang === "c++" || lang === "cpp" || lang === "g++") return "cpp";
    if (lang === "c#" || lang === "csharp") return "csharp";
    if (lang === "javascript" || lang === "js") return "javascript";
    if (lang === "python" || lang === "py") return "python";
    if (lang === "java") return "java";
    if (lang === "c") return "c";
    return lang;
};

const createInitialFile = (normalizedLanguage) => ({
    id: 1,
    name: `main${languageOptions[normalizedLanguage]?.fileExtension || ".py"}`,
    language: normalizedLanguage,
    content: defaultSnippets[normalizedLanguage] || defaultSnippets.python,
});

const CodeEditor = ({ problemStatement, activeContent }) => {
    const [cookies] = useCookies(['roleId', 'userId']);
    const { courseId, sectionId } = useParams();
    const [userId, setUserId] = useState(cookies.userId || null);
    const [userInput, setUserInput] = useState("");
    const [assistantLoading, setAssistantLoading] = useState(false);
    const normalizedLanguage = getNormalizedLanguage(activeContent?.language);
    const hasBeenSubmittedBefore = Boolean(activeContent?.submitReason) || Boolean(activeContent?.completed && activeContent?.codeSnippet);
    const [files, setFiles] = useState([{
        id: 1,
        name: `main${languageOptions[normalizedLanguage]?.fileExtension || ".py"}`,
        language: normalizedLanguage,
        content: (hasBeenSubmittedBefore && activeContent?.codeSnippet)
            ? activeContent.codeSnippet
            : (defaultSnippets[normalizedLanguage] || defaultSnippets.python),
    }]);
    const [activeFileId] = useState(1);
    const [output, setOutput] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const [reviewLoading, setReviewLoading] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(true);
    const [testResults, setTestResults] = useState([]);
    const [executionResult, setExecutionResult] = useState(null);
    const [activeTab, setActiveTab] = useState(0);
    
    const codeMirrorRef = useRef(null);
    const proctoringContainerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const lastSnapshotCodeRef = useRef(null);

    // ── Proctoring ─────────────────────────────────────────────────────────────
    const isProctored = activeContent?.isProctored === true;
    const [showProctoringStart, setShowProctoringStart] = useState(isProctored && !hasBeenSubmittedBefore);
    const [activeViolation, setActiveViolation] = useState(null);
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(hasBeenSubmittedBefore);
    const [showCompilerAfterSubmit, setShowCompilerAfterSubmit] = useState(false);
    const [idleEvents, setIdleEvents] = useState([]);
    const [codeSnapshots, setCodeSnapshots] = useState([]);
    const [pasteEvents, setPasteEvents] = useState([]);
    const [startedAt, setStartedAt] = useState(null);

    const handleViolation = useCallback((type) => {
        if (type === "copyAttempt") {
            setPasteEvents(prev => [...prev, { type: "pasteEvent", timestamp: new Date().toISOString() }]);
            toast.warn("Paste detected and logged during proctored session.");
        } else {
            setActiveViolation(type);
        }
    }, []);

    const { violationCounts, proctoringLogs, requestFullscreen, isContainerFullscreen } = useProctoring({
        enabled: isProctored && !showProctoringStart,
        onViolation: handleViolation,
        blockCopyPaste: false,
    });

    const totalViolations = Object.values(violationCounts).reduce((sum, n) => sum + n, 0);

    const handleStartProctoredSession = () => {
        requestFullscreen(proctoringContainerRef.current);
        setStartedAt(new Date().toISOString());
        setShowProctoringStart(false);
    };

    const handleResumeAfterViolation = useCallback(() => {
        // Always re-enter fullscreen on resume — the session requires it regardless of
        // which violation (fullscreenExit vs tabSwitch) last set activeViolation.
        if (proctoringContainerRef.current) {
            requestFullscreen(proctoringContainerRef.current);
        }
        setActiveViolation(null);
    }, [requestFullscreen]);

    const handleSubmitCode = useCallback(async (reason = 'timer') => {
        setIsSubmitted(true); // lock immediately — prevents timer + manual double-submit
        const currentCode = files.find(f => f.id === activeFileId)?.content || "";
        if (!activeContent?.contentId) {
            toast.error("Cannot submit: content ID is missing.");
            // console.error("Submit aborted — contentId is undefined", { activeContent });
            return;
        }
        try {
            const body = {
                courseId,
                sectionId,
                contentId: activeContent.contentId,
                completedStatus: true,
                userId: cookies.userId,
                codeSnippet: currentCode,
                output: reason === 'manual' ? "Manually submitted by student" : "Auto-submitted: timer expired",
                submitReason: reason,
                submittedAt: new Date().toISOString(),
                ...(isProctored && {
                    startedAt,
                    marksPerTestCase: activeContent?.marksPerTestCase || 1,
                    totalTestCases: activeContent?.testCases?.length || 0,
                    passedTestCases: testResults.filter(r => r.passed).length,
                    earnedMarks: (activeContent?.marksPerTestCase || 1) * testResults.filter(r => r.passed).length,
                    totalMarks: (activeContent?.marksPerTestCase || 1) * (activeContent?.testCases?.length || 0),
                    testResults: testResults.map(r => ({
                        index: r.index,
                        passed: r.passed,
                        input: r.input,
                        expectedOutput: r.expectedOutput,
                        actualOutput: r.actualOutput,
                    })),
                    codeProctoringData: {
                        violations: proctoringLogs,
                        idleEvents,
                        codeSnapshots,
                        pasteEvents,
                    },
                }),
            };
            // console.log("Submitting code sandbox progress:", body);
            await updateCodeSandboxProgress(body);
            if (reason === 'manual') {
                toast.success("✅ Solution submitted successfully!");
            } else {
                toast.warn("⏰ Time's up! Your code has been auto-submitted.");
            }
        } catch (err) {
            // console.error("Submit failed:", err);
            toast.error("Failed to submit. Please try again.");
        }
    }, [cookies, courseId, sectionId, activeContent, files, activeFileId, isProctored, proctoringLogs, idleEvents, codeSnapshots, pasteEvents, testResults, startedAt]);

    // Idle time tracking — flag after 2 min of no keyboard/mouse activity
    useEffect(() => {
        if (!isProctored || showProctoringStart) return;
        const IDLE_THRESHOLD_MS = 2 * 60 * 1000;
        const resetActivity = () => { lastActivityRef.current = Date.now(); };
        const checkIdle = setInterval(() => {
            const idle = Date.now() - lastActivityRef.current;
            if (idle > IDLE_THRESHOLD_MS) {
                setIdleEvents(prev => [...prev, {
                    type: "idleDetected",
                    timestamp: new Date().toISOString(),
                    idleDurationSeconds: Math.round(idle / 1000),
                }]);
                lastActivityRef.current = Date.now(); // reset to avoid spam
            }
        }, 30_000);
        document.addEventListener("keydown", resetActivity, true);
        document.addEventListener("mousemove", resetActivity, { passive: true });
        return () => {
            clearInterval(checkIdle);
            document.removeEventListener("keydown", resetActivity, true);
            document.removeEventListener("mousemove", resetActivity);
        };
    }, [isProctored, showProctoringStart]);

    // Code snapshots — save a snapshot every 3 min when code has changed
    useEffect(() => {
        if (!isProctored || showProctoringStart) return;
        const snap = setInterval(() => {
            const currentCode = files.find(f => f.id === activeFileId)?.content || "";
            if (currentCode !== lastSnapshotCodeRef.current && currentCode.trim()) {
                setCodeSnapshots(prev => [...prev.slice(-19), {
                    type: "codeSnapshot",
                    timestamp: new Date().toISOString(),
                    charCount: currentCode.length,
                    preview: currentCode.substring(0, 300),
                }]);
                lastSnapshotCodeRef.current = currentCode;
            }
        }, 3 * 60_000);
        return () => clearInterval(snap);
    }, [isProctored, showProctoringStart, files, activeFileId]);

    // Countdown timer — auto-submit when time runs out
    const timerMinutes = activeContent?.timerMinutes ? Number(activeContent.timerMinutes) : null;
    const [timeLeft, setTimeLeft] = useState(timerMinutes ? timerMinutes * 60 : null);

    useEffect(() => {
        if (!isProctored || showProctoringStart || timeLeft === null || isSubmitted) return;
        if (timeLeft <= 0) {
            handleSubmitCode();
            return;
        }
        const tick = setInterval(() => setTimeLeft(prev => (prev !== null ? prev - 1 : null)), 1000);
        return () => clearInterval(tick);
    }, [isProctored, showProctoringStart, timeLeft, isSubmitted]);

    const formatTime = (seconds) => {
        if (seconds === null) return null;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    };
    // ────────────────────────────────────────────────────────────────────────────

    const activeFile = files.find((file) => file.id === activeFileId);
    const activeLanguage = languageOptions[activeFile.language];
    const isCodeEmpty = !activeFile.content.trim() || activeFile.content === defaultSnippets[normalizedLanguage];

    const toggleTheme = () => setIsDarkMode((prevMode) => !prevMode);

    useEffect(() => {
        setFiles([createInitialFile(normalizedLanguage)]);
        setUserInput("");
        setOutput("");
        setError("");
        setTestResults([]);
        setExecutionResult(null);
    }, [problemStatement, normalizedLanguage]);

    const handleCodeChange = (value) => {
        setFiles((prevFiles) =>
            prevFiles.map((file) =>
                file.id === activeFileId ? { ...file, content: value } : file
            )
        );
    };

    const handleClearCode = () => {
        setFiles((prevFiles) =>
            prevFiles.map((file) =>
                file.id === activeFileId
                    ? { ...file, content: defaultSnippets[normalizedLanguage] }
                    : file
            )
        );
        setUserInput("");
        setOutput("");
        setError("");
        setTestResults([]);
        setExecutionResult(null);
    };

    const triggerCodeAssistance = async () => {
        setAssistantLoading(true);
        try {
            const view = codeMirrorRef.current?.view;
            if (view) {
                const triggered = startCompletion(view);
                if (!triggered) {
                    toast.warn("Code Assistance is not active or cannot be triggered in the current context.");
                }
            } else {
                toast.error("Code Editor is not yet ready.");
            }
        } catch (err) {
            toast.error("Error triggering code assistance: " + err.message);
        } finally {
            setTimeout(() => setAssistantLoading(false), 500);
        }
    };

    const handleRunCode = async () => {
        if (isCodeEmpty) {
            toast.warn("Please write some code before running!");
            return;
        }
        setLoading(true);
        setOutput("");
        setError("");
        setTestResults([]);
        setExecutionResult(null);
        
        try {
            if (typeof executeCode !== 'function') {
                throw new Error("executeCode function is not defined. Check ServerAPI/serverAPI import.");
            }

            const additionalFiles = [];
            if (activeContent?.fileRequired) {
                const signedUrlPayload = {
                    userId: cookies.userId,
                    courseId,
                    sectionId,
                    fileName: activeContent.fileName,
                    method: "GET",
                };
                const signedUrlResponse = await getSignedUrl(signedUrlPayload);
                if (!signedUrlResponse?.data?.preSignedUrl) {
                    throw new Error("preSignedUrl not found from getSignedUrl response");
                }
                const fileResp = await fetch(signedUrlResponse.data.preSignedUrl, { method: "GET" });
                if (!fileResp.ok) {
                    throw new Error(`Failed to fetch file: ${fileResp.status}`);
                }
                const fileExtension = activeContent.fileName.split('.').pop().toLowerCase();
                if (['pdf', 'xlsx', 'docx'].includes(fileExtension)) {
                    const arrayBuffer = await fileResp.arrayBuffer();
                    const blob = new Blob([arrayBuffer]);
                    const base64Content = await new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = () => resolve(reader.result.split(',')[1]);
                        reader.onerror = reject;
                        reader.readAsDataURL(blob);
                    });
                    additionalFiles.push({
                        name: activeContent.fileName,
                        content: base64Content,
                        encoding: "base64",
                    });
                } else {
                    const fileContent = await fileResp.text();
                    additionalFiles.push({
                        name: activeContent.fileName,
                        content: fileContent,
                    });
                }
            }

            const executionLanguage = activeFile.language === 'cpp' ? 'g++' : 
            activeFile.language === 'c' ? 'c' : 
            activeFile.language === 'csharp' ? 'c#' :
            activeFile.language;
const fileName = activeFile.language === 'cpp' ? 'main.cpp' : 
   activeFile.language === 'c' ? 'main.c' : 
   activeFile.language === 'java' ? 'Main.java' :
   activeFile.language === 'csharp' ? 'Program.cs' :
   `main${languageOptions[activeFile.language]?.fileExtension || ".py"}`;
   
            const basePayload = {
                language: executionLanguage,
                version: activeContent?.version || defaultVersions[normalizedLanguage] || "10.2.0",
                files: [
                    { 
                        name: fileName,
                        content: activeFile.content.trim() 
                    },
                    ...additionalFiles,
                ],
                contentId: activeContent?.contentId || "",
                courseId: courseId || "",
                sectionId: sectionId || "",
                userId: cookies.userId || "",
                stdin: "",
            };

            const hasValidTestCases = activeContent?.testCases &&
                Array.isArray(activeContent.testCases) &&
                activeContent.testCases.length > 0 &&
                activeContent.testCases.every(
                    (testCase) => typeof testCase.testInput === 'string' &&
                        testCase.testInput.trim() !== '' &&
                        typeof testCase.expectedOutput === 'string' &&
                        testCase.expectedOutput.trim() !== ''
                );
            const hasUserInput = userInput.trim() !== "";

            // console.log("activeContent.testCases:", activeContent?.testCases);
            // console.log("hasValidTestCases:", hasValidTestCases, "hasUserInput:", hasUserInput, "userInput:", userInput);

            if (hasValidTestCases) {
                const outputLines = [];
                const results = [];
                
                for (const [index, testCase] of activeContent.testCases.entries()) {
                    outputLines.push(`🔄 Running test case ${index + 1}...\n`);
                    setOutput(outputLines.join(""));
                    
                    const payload = { 
                        ...basePayload, 
                        stdin: testCase.testInput 
                    };
                    
                    // console.log("Test case payload:", payload);
                    const result = await executeCode(payload);
                    
                    if (!result?.run || typeof result.run.code === 'undefined') {
                        throw new Error("Invalid response format: Missing 'run' or 'code' property.");
                    }
                    
                    const { code: executionCode, stdout = '', stderr = '' } = result.run;
                    const testError = stderr ? stderr.trim() : stdout && executionCode !== 0 ? stdout.trim() : '';
                    
                    if (executionCode !== 0) {
                        const testResult = { 
                            index: index + 1, 
                            passed: false, 
                            expected: testCase.expectedOutput, 
                            got: testError,
                            input: testCase.testInput,
                            error: true
                        };
                        results.push(testResult);
                        outputLines.push(`❌ Test ${index + 1}: Failed\n${testError}\n`);
                        setOutput(outputLines.join(""));
                        continue;
                    }
                    
                    const gotOutput = stdout.trim();
                    const expectedOutput = testCase.expectedOutput.trim();
                    let passed = gotOutput === expectedOutput;
                    if (normalizedLanguage === 'csharp') {
                        try {
                            const parsedGot = JSON.parse(gotOutput);
                            const parsedExpected = JSON.parse(expectedOutput);
                            passed = JSON.stringify(parsedGot) === JSON.stringify(parsedExpected);
                        } catch (e) {
                            passed = gotOutput === expectedOutput;
                        }
                    }
                    const testResult = { 
                        index: index + 1, 
                        passed, 
                        expected: testCase.expectedOutput, 
                        got: gotOutput,
                        input: testCase.testInput,
                        error: false
                    };
                    results.push(testResult);
                    
                    if (passed) {
                        outputLines.push(`✅ Test ${index + 1}: Passed\n`);
                    } else {
                        outputLines.push(`❌ Test ${index + 1}: Failed\nExpected: ${testCase.expectedOutput}\nGot: ${gotOutput}\n`);
                    }
                    setOutput(outputLines.join(""));
                }
                
                setTestResults(results);
                const allPassed = results.every((r) => r.passed);
                const passedCount = results.filter((r) => r.passed).length;
                const totalCount = results.length;
                
                if (allPassed && results.length > 0) {
                    outputLines.push(`\n🎉 All test cases passed! (${passedCount}/${totalCount})`);
                    setOutput(outputLines.join(""));
                    toast.success(`All test cases passed! (${passedCount}/${totalCount})`);
                    if (cookies.roleId === ROLES.COURSE_PARTICIPANT && activeContent?.completedStatus !== true) {
                        const body = {
                            courseId,
                            sectionId,
                            contentId: activeContent?.contentId,
                            completedStatus: true,
                            userId: cookies.userId,
                            codeSnippet: activeFile.content,
                            output: outputLines,
                            ...(isProctored && { codeProctoringData: { violations: proctoringLogs, idleEvents, codeSnapshots, pasteEvents } }),
                        };
                        await updateCodeSandboxProgress(body);
                    }
                } else if (results.length > 0) {
                    outputLines.push(`\n📊 Test Results: ${passedCount}/${totalCount} passed`);
                    setOutput(outputLines.join(""));
                    toast.error(`Some test cases failed. (${passedCount}/${totalCount} passed)`);
                } else {
                    toast.warn("No valid test cases provided.");
                }
            } else {
                const payload = { ...basePayload, stdin: hasUserInput ? userInput : "" };
                // console.log("Executing code with payload:", payload);
                const result = await executeCode(payload);
                
                if (!result?.run || typeof result.run.code === 'undefined') {
                    throw new Error("Invalid response format: Missing 'run' or 'code' property.");
                }
                
                const { code: executionCode, stdout = '', stderr = '' } = result.run;
                const executionResultData = {
                    stdout: stdout.trim(),
                    stderr: stderr.trim(),
                    code: executionCode,
                };
                setExecutionResult(executionResultData);
                
                if (executionCode === 0) {
                    const outputMessage = stdout.trim() || "[Code executed with no output]";
                    setOutput(outputMessage);
                    setError("");
                    toast.success("Code executed successfully!");
                    if (cookies.roleId === ROLES.COURSE_PARTICIPANT && activeContent?.completedStatus !== true) {
                        const body = {
                            courseId,
                            sectionId,
                            contentId: activeContent?.contentId,
                            completedStatus: true,
                            userId: cookies.userId,
                            codeSnippet: activeFile.content,
                            output: outputMessage,
                            ...(isProctored && { codeProctoringData: { violations: proctoringLogs, idleEvents, codeSnapshots, pasteEvents } }),
                        };
                        await updateCodeSandboxProgress(body);
                    }
                } else {
                    const errorMessage = stderr ? stderr.trim() : stdout ? stdout.trim() : "Code execution failed. Please check your code.";
                    setOutput(errorMessage);
                    setError(errorMessage);
                    toast.error(`Code execution failed: ${errorMessage.split('\n')[0]}`);
                }
            }
        } catch (err) {
            const errorMessage = err.response?.data?.error || "Error executing code. Please try again.";
            setOutput(errorMessage);
            setError(errorMessage);
            setExecutionResult({ error: errorMessage });
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleReviewCode = async () => {
        if (isCodeEmpty) {
            toast.warn("Please write some code before requesting a review!");
            return;
        }
        setReviewLoading(true);
        try {
            const payload = {
                language: activeFile.language,
                files: [{ name: activeFile.language === 'cpp' ? 'main.cpp' : activeFile.language === 'c' ? 'main.c' : activeFile.language === 'java' ? 'Main.java' : `main${languageOptions[activeFile.language]?.fileExtension || ".py"}`, content: activeFile.content.trim() }],
                contentId: activeContent?.contentId || "",
                courseId: courseId || "",
                sectionId: sectionId || "",
                userId: cookies.userId || "",
                version: activeContent?.version,
            };

            await handleCodeReview(payload);
            toast.success("Code review PDF downloaded successfully!");
        } catch (err) {
            // console.error("Error in handleReviewCode:", err);
            toast.error(`Failed to generate code review PDF: ${err.message}`);
        } finally {
            setReviewLoading(false);
        }
    };

    const extensions = [
        activeLanguage.extension(),
        ...createAssistanceExtensions(
            problemStatement,
            activeFile.language,
            activeContent?.testCases || [],
            userId,
            setUserId
        ),
    ];

    // Fixed Styles - No more overflow
    const rootStyles = {
        margin: '0',
        marginTop: isContainerFullscreen ? '0' : '1rem',
        marginLeft: isContainerFullscreen ? '0' : '390px',
        width: isContainerFullscreen ? '100%' : 'calc(100vw - 390px - 1rem)',
        minWidth: isContainerFullscreen ? 'unset' : '800px',
        maxWidth: isContainerFullscreen ? '100%' : 'calc(100vw - 390px - 1rem)',
        minHeight: isContainerFullscreen ? '100vh' : 'calc(100vh - 200px)',
        position: 'relative',
        borderRadius: isContainerFullscreen ? '0' : '8px',
        background: isDarkMode ? '#1a202c' : '#f0f2f5',
        boxSizing: 'border-box',
        zIndex: 49,
        overflow: 'hidden',
    };

    const containerStyles = {
        padding: '0',
        margin: '0',
        width: '100%',
        boxSizing: 'border-box',
        height: '100%',
        overflow: 'hidden', // Prevent container overflow
    };

    const problemStatementStyles = {
        padding: '1rem 1.5rem',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        maxHeight: '150px',
        overflowY: 'auto',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        color: isDarkMode ? '#d1d5db' : '#4a4a4a',
        background: isDarkMode ? '#1a202c' : 'linear-gradient(to right, #e3f2fd, #bbdefb)',
        width: '100%',
        boxSizing: 'border-box',
    };

    const problemTitleStyles = {
        fontSize: '1.35rem',
        fontWeight: 700,
        color: isDarkMode ? '#93c5fd' : '#1976d2',
        marginBottom: '0.25rem',
    };

    const problemTextStyles = {
        color: isDarkMode ? '#d1d5db' : '#4a4a4a',
        fontSize: '0.9rem',
        lineHeight: '1.5',
        margin: '0',
        whiteSpace: 'pre-line',
    };

    const mainContentStyles = {
        width: '100%',
        height: 'calc(100vh - 250px)',
        padding: '0.75rem',
        boxSizing: 'border-box',
        display: 'flex',
        gap: '0.75rem',
        overflow: 'hidden', // Prevent main content overflow
    };

    const codeEditorStyles = {
        flex: '0 0 69%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0, // Important for flexbox overflow
    };

    const codeEditorContainerStyles = {
        borderRadius: '8px',
        border: isDarkMode ? '1px solid #4a5568' : '1px solid #cccccc',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0, // Important for flexbox overflow
    };

    const editorHeaderStyles = {
        height: '30px',
        fontSize: '14px',
        fontWeight: 600,
        padding: '8px 16px',
        color: isDarkMode ? 'white' : '#333333',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        background: isDarkMode ? 'linear-gradient(90deg, #252544 0%, #252544 100%)' : 'linear-gradient(90deg, #e0e7ff 0%, #c3daff 100%)',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0, // Prevent header from shrinking
    };

    const codeMirrorStyles = {
        flex: 1,
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        color: isDarkMode ? '#e2e8f0' : '#1f2937',
        width: '100%',
        boxSizing: 'border-box',
        overflow: 'hidden',
        minWidth: 0, // Important for flexbox overflow
    };

    const inputOutputContainerStyles = {
        flex: '0 0 31%',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0, // Important for flexbox overflow
    };

    const inputOutputInnerStyles = {
        borderRadius: '8px',
        border: isDarkMode ? '1px solid #4a5568' : '1px solid #cccccc',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        width: '100%',
        boxSizing: 'border-box',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minWidth: 0, // Important for flexbox overflow
    };

    // Completely redesigned Controls Container
    const controlsContainerStyles = {
        background: isDarkMode ? 'linear-gradient(90deg, #252544 0%, #252544 100%)' : 'linear-gradient(90deg, #e0e7ff 0%, #c3daff 100%)',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        padding: '8px 16px',
        height: '60px',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        flexShrink: 0,
    };

    // Completely redesigned Button Styles with larger size and perfect centering
    const createButtonStyle = (bgColor) => ({
        fontWeight: 600,
        borderRadius: '10px',
        width: '50px',
        height: '50px',
        minWidth: '50px',
        minHeight: '50px',
        padding: '0',
        margin: '0',
        border: 'none',
        boxShadow: '0 3px 6px rgba(0, 0, 0, 0.2)',
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: bgColor,
        color: 'white',
        '&:hover': {
            transform: 'translateY(-2px) scale(1.05)',
            boxShadow: '0 6px 12px rgba(0, 0, 0, 0.3)',
        },
        '&:active': {
            transform: 'translateY(0) scale(1)',
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
            transform: 'none',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        '& .MuiButton-startIcon': {
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
        },
        '& .MuiCircularProgress-root': {
            width: '24px !important',
            height: '24px !important',
        }
    });

    const executeButtonStyles = createButtonStyle(
        isDarkMode ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
    );

    const clearButtonStyles = createButtonStyle(
        isDarkMode ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' : 'linear-gradient(135deg, #f87171 0%, #ef4444 100%)'
    );

    const reviewButtonStyles = createButtonStyle(
        isDarkMode ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'
    );

    const themeToggleButtonStyles = createButtonStyle(
        isDarkMode ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    );

    const assistanceButtonStyles = createButtonStyle(
        'linear-gradient(135deg, #a78bfa 0%, #8b5cf6 100%)'
    );

    const tabsContainerStyles = {
        background: isDarkMode ? 'linear-gradient(90deg, #252544 0%, #252544 100%)' : 'linear-gradient(90deg, #e0e7ff 0%, #c3daff 100%)',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
        padding: '0',
        height: '40px',
        boxSizing: 'border-box',
        flexShrink: 0,
    };

    const tabStyles = {
        color: isDarkMode ? '#e5e7eb' : '#374151',
        fontSize: '0.85rem',
        fontWeight: 600,
        textTransform: 'none',
        padding: '10px 16px',
        transition: 'all 0.2s ease',
        height: '40px',
        lineHeight: '20px',
        minHeight: '40px',
        '&:hover': {
            background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
        },
        '&.Mui-selected': {
            color: isDarkMode ? '#93c5fd' : '#1e40af',
            fontWeight: 700,
            background: isDarkMode ? 'rgba(147, 197, 253, 0.15)' : 'rgba(29, 78, 216, 0.1)',
        },
    };

    const contentAreaStyles = {
        flex: 1,
        padding: '0.75rem',
        overflowY: 'auto',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        boxSizing: 'border-box',
        minWidth: 0, // Important for flexbox overflow
    };

    const accordionStyles = {
        background: isDarkMode ? '#252544' : '#f8fafc',
        borderRadius: '6px',
        marginBottom: '0.5rem',
        boxShadow: isDarkMode ? '0 2px 6px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        boxSizing: 'border-box',
        '&:before': {
            display: 'none',
        },
    };

    const accordionSummaryStyles = {
        background: isDarkMode ? 'linear-gradient(90deg, #252544 0%, #252544 100%)' : 'linear-gradient(90deg, #e0e7ff 0%, #c3daff 100%)',
        borderRadius: '6px',
        padding: '0 0.75rem',
        minHeight: '36px',
        height: '36px',
        '& .MuiAccordionSummary-content': {
            margin: '0',
            display: 'flex',
            alignItems: 'center',
        }
    };

    const accordionDetailsStyles = {
        padding: '0.75rem',
        background: isDarkMode ? '#1f2937' : '#ffffff',
        borderRadius: '0 0 6px 6px',
        boxSizing: 'border-box',
        maxHeight: '150px',
        overflowY: 'auto',
    };

    const testCaseItemStyles = {
        padding: '0.5rem',
        background: isDarkMode ? '#252544' : '#f9fafb',
        borderRadius: '4px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
        marginBottom: '0.5rem',
        boxSizing: 'border-box',
        width: '100%',
    };

    const testCaseHeaderStyles = {
        fontSize: '0.9rem',
        fontWeight: 600,
        color: isDarkMode ? '#93c5fd' : '#1e40af',
        paddingBottom: '0.25rem',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    };

    const testCaseContentStyles = {
        fontFamily: '"Fira Code", monospace',
        fontSize: '0.85rem',
        color: isDarkMode ? '#e5e7eb' : '#1f2937',
        lineHeight: '1.4',
        padding: '0.5rem',
        border: isDarkMode ? '1px solid #4b5568' : '1px solid #d1d5db',
        borderRadius: '4px',
        marginBottom: '0.25rem',
        boxSizing: 'border-box',
        wordBreak: 'break-word',
    };

    const testCaseLabelStyles = {
        fontWeight: 600,
        color: isDarkMode ? '#93c5fd' : '#1e40af',
        marginRight: '0.5rem',
    };

    const testResultItemStyles = {
        padding: '0.5rem',
        background: isDarkMode ? '#252544' : '#f9fafb',
        borderRadius: '4px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
        marginBottom: '0.5rem',
        boxSizing: 'border-box',
        width: '100%',
    };

    const testResultHeaderStyles = {
        fontSize: '0.9rem',
        fontWeight: 600,
        color: isDarkMode ? '#e5e7eb' : '#1f2937',
        paddingBottom: '0.25rem',
        borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e5e7eb',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    };

    const testResultContentStyles = {
        fontFamily: '"Fira Code", monospace',
        fontSize: '0.85rem',
        color: isDarkMode ? '#e5e7eb' : '#1f2937',
        lineHeight: '1.4',
        padding: '0.5rem',
        border: isDarkMode ? '1px solid #4b5568' : '1px solid #d1d5db',
        borderRadius: '4px',
        marginBottom: '0.25rem',
        boxSizing: 'border-box',
        wordBreak: 'break-word',
    };

    const outputContentStyles = {
        background: isDarkMode ? '#18181b' : '#f3f4f6',
        padding: '1rem',
        borderRadius: '4px',
        border: isDarkMode ? '1px solid #4b5568' : '1px solid #d1d5db',
        fontFamily: '"Fira Code", monospace',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: isDarkMode ? '#e5e7eb' : '#1f2937',
        fontSize: '0.9rem',
        lineHeight: '1.5',
        height: '100%',
        overflowY: 'auto',
        boxSizing: 'border-box',
        minHeight: '200px',
    };

    const outputPreStyles = {
        margin: '0',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        color: error ? '#ef4444' : isDarkMode ? '#e5e7eb' : '#1f2937',
        fontSize: '0.9rem',
        lineHeight: '1.5',
    };

    const customInputStyles = {
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
        '& .MuiOutlinedInput-root': {
            height: '100%',
            fontFamily: '"Fira Code", monospace',
            fontSize: '0.85rem',
            alignItems: 'flex-start',
            '& fieldset': {
                border: isDarkMode ? '1px solid #4b5568' : '1px solid #d1d5db',
                borderRadius: '4px',
            },
            '&:hover fieldset': {
                borderColor: isDarkMode ? '#93c5fd' : '#1e40af',
            },
            '&.Mui-focused fieldset': {
                borderColor: isDarkMode ? '#93c5fd' : '#1e40af',
                boxShadow: isDarkMode ? '0 0 0 2px rgba(147, 197, 253, 0.2)' : '0 0 0 2px rgba(29, 78, 216, 0.2)',
            },
        },
        '& .MuiOutlinedInput-input': {
            color: isDarkMode ? '#e5e7eb' : '#1f2937',
            background: isDarkMode ? '#18181b' : '#f3f4f6',
            borderRadius: '4px',
            padding: '0.75rem',
            boxSizing: 'border-box',
            height: '100% !important',
            minHeight: '200px',
        },
    };

    const emptyStateStyles = {
        textAlign: 'center',
        height: '100%',
        color: isDarkMode ? '#9ca3af' : '#6b7280',
        padding: '1.5rem',
        fontStyle: 'italic',
        fontSize: '0.9rem',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.5rem',
    };

    return (
        <section ref={proctoringContainerRef} style={rootStyles}>
            {isProctored && (
                <ProctoredCodeEditor
                    isDarkMode={isDarkMode}
                    proctoringContainerRef={proctoringContainerRef}
                    showProctoringStart={showProctoringStart}
                    handleStartProctoredSession={handleStartProctoredSession}
                    showSubmitConfirm={showSubmitConfirm}
                    setShowSubmitConfirm={setShowSubmitConfirm}
                    handleSubmitCode={handleSubmitCode}
                    activeViolation={activeViolation}
                    totalViolations={totalViolations}
                    handleResumeAfterViolation={handleResumeAfterViolation}
                    isSubmitted={isSubmitted}
                    showCompilerAfterSubmit={showCompilerAfterSubmit}
                    setShowCompilerAfterSubmit={setShowCompilerAfterSubmit}
                    timeLeft={timeLeft}
                    formatTime={formatTime}
                    proctoringLogs={proctoringLogs}
                    pasteEvents={pasteEvents}
                    activeContent={activeContent}
                    activeLanguage={activeLanguage}
                    problemStatement={problemStatement}
                    activeFile={activeFile}
                    loading={loading}
                    isCodeEmpty={isCodeEmpty}
                    handleRunCode={handleRunCode}
                    handleClearCode={handleClearCode}
                    toggleTheme={toggleTheme}
                    codeMirrorRef={codeMirrorRef}
                    extensions={extensions}
                    handleCodeChange={handleCodeChange}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    testResults={testResults}
                    executionResult={executionResult}
                    output={output}
                    error={error}
                    userInput={userInput}
                    setUserInput={setUserInput}
                />
            )}
            {!isProctored && (
                /* ── Original layout (non-proctored) — unchanged ── */
                <div style={containerStyles}>
                    <div style={problemStatementStyles}>
                        <h2 style={problemTitleStyles}>Solve the following problem using {activeLanguage.name} language.</h2>
                        <p style={problemTextStyles}>{problemStatement || "No problem statement provided."}</p>
                    </div>
                    <div style={mainContentStyles}>
                        <section style={codeEditorStyles}>
                            <div style={codeEditorContainerStyles}>
                                <div style={editorHeaderStyles}>
                                    Code Editor ({activeLanguage.name})
                                </div>
                                <CodeMirror
                                    ref={codeMirrorRef}
                                    value={activeFile.content}
                                    height="100%"
                                    extensions={extensions}
                                    onChange={handleCodeChange}
                                    theme={isDarkMode ? oneDark : defaultLightThemeOption}
                                    style={codeMirrorStyles}
                                />
                            </div>
                        </section>
                        <section style={inputOutputContainerStyles}>
                            <div style={inputOutputInnerStyles}>
                                <div style={controlsContainerStyles}>
                                    <Tooltip title="Execute code" arrow>
                                        <span>
                                            <Button sx={executeButtonStyles} onClick={handleRunCode} disabled={loading || reviewLoading || isCodeEmpty}>
                                                {loading ? <CircularProgress size={24} color="inherit" /> : <Play size={24} />}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Clear" arrow>
                                        <span>
                                            <Button sx={clearButtonStyles} onClick={handleClearCode} disabled={loading || reviewLoading || isCodeEmpty}>
                                                <Trash2 size={24} />
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Review Code" arrow>
                                        <span>
                                            <Button sx={reviewButtonStyles} onClick={handleReviewCode} disabled={loading || reviewLoading || isCodeEmpty}>
                                                {reviewLoading ? <CircularProgress size={24} color="inherit" /> : <FileText size={24} />}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Toggle Theme" arrow>
                                        <span>
                                            <Button sx={themeToggleButtonStyles} onClick={toggleTheme} disabled={loading || reviewLoading}>
                                                {isDarkMode ? <Sun size={24} /> : <Moon size={24} />}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="AI Code Assistance (Ctrl+Space)" arrow>
                                        <span>
                                            <Button sx={assistanceButtonStyles} onClick={triggerCodeAssistance} disabled={loading || reviewLoading || assistantLoading}>
                                                {assistantLoading ? <CircularProgress size={24} color="inherit" /> : <Bot size={24} />}
                                            </Button>
                                        </span>
                                    </Tooltip>
                                </div>
                                <div style={tabsContainerStyles}>
                                    <Tabs
                                        value={activeTab}
                                        onChange={(e, newValue) => setActiveTab(newValue)}
                                        TabIndicatorProps={{ style: { backgroundColor: isDarkMode ? '#93c5fd' : '#1e40af', height: '3px', borderRadius: '3px' } }}
                                        sx={{ minHeight: '40px' }}
                                    >
                                        <Tab label="Test Cases" sx={tabStyles} />
                                        <Tab label={`Test Results (${testResults.filter(r => r.passed).length}/${testResults.length})`} sx={tabStyles} />
                                        <Tab label="Custom Input" sx={tabStyles} />
                                        <Tab label="Output" sx={tabStyles} />
                                    </Tabs>
                                </div>
                                <div style={contentAreaStyles}>
                                    {activeTab === 0 && (
                                        <div>
                                            {activeContent?.testCases && activeContent.testCases.length > 0 ? (
                                                activeContent.testCases.map((test, index) => (
                                                    <Accordion key={index} style={accordionStyles}>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? '#e5e7eb' : '#1f2937' }} />}
                                                            style={accordionSummaryStyles}
                                                        >
                                                            <Typography style={{ fontWeight: 600, color: isDarkMode ? '#93c5fd' : '#1e40af', fontSize: '0.9rem' }}>
                                                                Test Case {index + 1}
                                                            </Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails style={accordionDetailsStyles}>
                                                            <div style={testCaseItemStyles}>
                                                                <div style={testCaseContentStyles}>
                                                                    <span style={testCaseLabelStyles}>Input:</span>
                                                                    {test.testInput || "[Empty]"}
                                                                </div>
                                                                <div style={testCaseContentStyles}>
                                                                    <span style={testCaseLabelStyles}>Expected Output:</span>
                                                                    {test.expectedOutput || "[Empty]"}
                                                                </div>
                                                            </div>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                ))
                                            ) : (
                                                <div style={emptyStateStyles}>
                                                    <FileText size={36} />
                                                    <div>No test cases available.</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {activeTab === 1 && (
                                        <div>
                                            {testResults.length > 0 ? (
                                                testResults.map((result) => (
                                                    <Accordion key={result.index} style={{
                                                        ...accordionStyles,
                                                        border: `2px solid ${result.passed ? '#10b981' : result.error ? '#f59e0b' : '#ef4444'}`,
                                                    }}>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? '#e5e7eb' : '#1f2937' }} />}
                                                            style={accordionSummaryStyles}
                                                        >
                                                            <div style={testResultHeaderStyles}>
                                                                {result.passed ? (
                                                                    <CheckCircle color="#10b981" size={18} />
                                                                ) : result.error ? (
                                                                    <AlertTriangle color="#f59e0b" size={18} />
                                                                ) : (
                                                                    <XCircle color="#ef4444" size={18} />
                                                                )}
                                                                <span>Test {result.index}: {result.passed ? 'Passed' : result.error ? 'Error' : 'Failed'}</span>
                                                            </div>
                                                        </AccordionSummary>
                                                        <AccordionDetails style={accordionDetailsStyles}>
                                                            <div style={testResultItemStyles}>
                                                                <div style={testResultContentStyles}>
                                                                    <span style={testCaseLabelStyles}>Input:</span>
                                                                    {result.input || "[Empty]"}
                                                                </div>
                                                                <div style={testResultContentStyles}>
                                                                    <span style={testCaseLabelStyles}>Expected:</span>
                                                                    {result.expected}
                                                                </div>
                                                                <div style={testResultContentStyles}>
                                                                    <span style={testCaseLabelStyles}>{result.error ? 'Error:' : 'Actual Output:'}</span>
                                                                    <span style={{
                                                                        color: result.error ? '#f59e0b' : result.passed ? '#10b981' : '#ef4444',
                                                                    }}>
                                                                        {result.got}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                ))
                                            ) : executionResult ? (
                                                <div style={testResultItemStyles}>
                                                    <div style={testResultHeaderStyles}>
                                                        <span>Execution Result</span>
                                                    </div>
                                                    {executionResult.stdout && (
                                                        <div style={testResultContentStyles}>
                                                            <span style={testCaseLabelStyles}>Output:</span>
                                                            {executionResult.stdout || "[Empty]"}
                                                        </div>
                                                    )}
                                                    {executionResult.stderr && (
                                                        <div style={testResultContentStyles}>
                                                            <span style={testCaseLabelStyles}>Error:</span>
                                                            <span style={{ color: '#f59e0b' }}>{executionResult.stderr}</span>
                                                        </div>
                                                    )}
                                                    {executionResult.error && (
                                                        <div style={testResultContentStyles}>
                                                            <span style={testCaseLabelStyles}>Error:</span>
                                                            <span style={{ color: '#ef4444' }}>{executionResult.error}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : (
                                                <div style={emptyStateStyles}>
                                                    <FileText size={36} />
                                                    <div>No test results available.</div>
                                                    <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                                                        Run your code to see test results here.
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {activeTab === 2 && (
                                        <div style={{ height: '100%', boxSizing: 'border-box' }}>
                                            <TextField
                                                placeholder="Enter input for stdin..."
                                                onChange={(e) => {
                                                    // console.log("Custom input updated:", e.target.value);
                                                    setUserInput(e.target.value);
                                                }}
                                                multiline
                                                rows={8}
                                                value={userInput}
                                                variant="outlined"
                                                sx={customInputStyles}
                                            />
                                        </div>
                                    )}
                                    {activeTab === 3 && (
                                        <div style={outputContentStyles}>
                                            <pre style={outputPreStyles}>
                                                {output || error || "No output to display. Run your code to see output here."}
                                            </pre>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </section>
                    </div>
                    {!isProctored && (
                        <div className="ai-chat-sidebar">
                            <ChatComponent
                                problemStatement={problemStatement}
                                codeSnippet={activeFile.content}
                                language={activeFile.language}
                                userId={userId}
                                output={output}
                                testCases={activeContent?.testCases || []}
                            />
                        </div>
                    )}
                </div>
            )}
        </section>
    );
};

export default CodeEditor;
